import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { User, Trash2, ShieldCheck } from "lucide-react";
import { ROLE_COLORS } from "./shared";
import PermissionsModal from "./PermissionsModal";

const ROLE_LABELS = { user: "Customer", admin: "Admin", super_admin: "Super Admin", driver: "Driver" };

export default function UsersTab({ users, currentUserId, updateUserRole, deleteUser, onRefresh }) {
  const [editingPermissionsFor, setEditingPermissionsFor] = useState(null);

  return (
    <div data-testid="users-tab">
      <h2 className="text-xl font-semibold text-white mb-2">Staff ({users.length})</h2>
      <p className="text-white/40 text-xs mb-6">
        Operators, admins and drivers. Customers (people who only book) live in the Customers tab.
        Click the <ShieldCheck className="inline w-3.5 h-3.5 text-[#C5A059] -mt-0.5" /> icon next to an admin to fine-tune their capabilities.
      </p>
      <div className="overflow-x-auto">
        <table className="data-table" data-testid="users-table">
          <thead><tr><th>User</th><th>Email</th><th>Role</th><th>Joined</th><th>Action</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id}>
                <td>
                  <div className="flex items-center gap-3">
                    {u.picture ? <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" /> :
                      <div className="w-8 h-8 rounded-full bg-[#C5A059]/20 flex items-center justify-center"><User className="w-4 h-4 text-[#C5A059]" /></div>}
                    <span className="text-white">{u.name}</span>
                  </div>
                </td>
                <td className="text-white/60">{u.email}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <span className={`px-2 py-1 rounded text-xs uppercase ${ROLE_COLORS[u.role] || ROLE_COLORS.user}`}>{ROLE_LABELS[u.role] || u.role}</span>
                    {u.role === "admin" && (
                      <span className="text-white/40 text-[10px]" title="Fine-grained permissions apply — click the shield icon to edit">
                        · {(u.permissions || []).length} perm{((u.permissions || []).length === 1) ? "" : "s"}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-white/50 font-mono text-xs">{u.created_at?.split("T")[0]}</td>
                <td>
                  {u.user_id === currentUserId ? <span className="text-white/30 text-xs">You</span> : (
                    <div className="flex items-center gap-2">
                      <Select value={u.role} onValueChange={(v) => updateUserRole(u.user_id, v)}>
                        <SelectTrigger className="w-36 h-8 bg-[#1F1F1F] border-[#333] text-white text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1F1F1F] border-[#333]">
                          <SelectItem value="user" className="text-white hover:bg-[#333] text-xs">Customer (demote)</SelectItem>
                          <SelectItem value="admin" className="text-white hover:bg-[#333] text-xs">Admin</SelectItem>
                          <SelectItem value="super_admin" className="text-white hover:bg-[#333] text-xs">Super Admin</SelectItem>
                          <SelectItem value="driver" className="text-white hover:bg-[#333] text-xs">Driver</SelectItem>
                        </SelectContent>
                      </Select>
                      {u.role === "admin" && (
                        <button
                          onClick={() => setEditingPermissionsFor(u)}
                          className="p-1.5 text-white/40 hover:text-[#C5A059] transition-colors"
                          title="Edit permissions"
                          data-testid={`edit-permissions-${u.user_id}`}
                        >
                          <ShieldCheck className="w-4 h-4" />
                        </button>
                      )}
                      {deleteUser && (
                        <button
                          onClick={() => deleteUser(u)}
                          className="p-1.5 text-white/40 hover:text-red-400 transition-colors"
                          title="Delete user (irreversible)"
                          data-testid={`delete-user-${u.user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingPermissionsFor && (
        <PermissionsModal
          user={editingPermissionsFor}
          onClose={() => setEditingPermissionsFor(null)}
          onSaved={() => { setEditingPermissionsFor(null); onRefresh?.(); }}
        />
      )}
    </div>
  );
}
